import { Contract, ContractCompanySpecificService, CreateContract } from "@/types";
import { EditContractInput } from "../validation/editContract";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import dayjs from "dayjs";
import { isPaymentReceiverInverted } from "@/utils/Contracts";

export function ContractToEditContractForm(contract: Contract) {
  const selectedServiceIds = contract.selectedServices.map((service) => service.id);
  const companySpecificServiceIds = contract.companySpecificServices.map((service) => service.companySpecificServiceId);

  const contractInputForm: EditContractInput = {
    name: contract.name,
    description: contract.description,
    contractBuyers: contract.contractBuyers.map((buyer) => buyer.id),
    contractSellers: contract.contractSellers.map((seller) => seller.id),
    contractDate: dayjs(contract.contractDate).toDate(),
    contractStartDate: dayjs(contract.contractStartDate).toDate(),
    contractStatus: contract.contractStatus as ContractStatusEnum,
    contractEndDate: contract.contractEndDate ? dayjs(contract.contractEndDate).toDate() : null,
    companySpecificProductId: contract.contractProduct.companySpecificProduct.id,
    useTransportServices: contract.useTransportServices,
    amount: contract.amount ? contract.amount.toString() : "0",
    agreedBuyerPricePrUnit: Math.abs(
      contract.contractProduct.agreedBuyerPricePrUnit[contract.contractProduct.currencyType]
    ),
    agreedSellerPricePrUnit: Math.abs(
      contract.contractProduct.agreedSellerPricePrUnit[contract.contractProduct.currencyType]
    ),
    selectedServices: selectedServiceIds,
    companySpecificServices: contract.companySpecificServices.map((service) => {
      const obj: ContractCompanySpecificService = {
        companySpecificServiceId: service.companySpecificServiceId,
        price: service.price,
        currencyType: service.currencyType,
      };
      return obj;
    }),
    allSelectedServices: [...selectedServiceIds, ...companySpecificServiceIds],
    invoiceIntervalId: contract.invoiceInterval.id,
    invoiceDueDateId: contract.invoiceDueDate.id,
    transportResponsible: contract.transportResponsible,
    currencyType: contract.contractProduct.currencyType,
    comment: contract.comment,
    amountUnitId: contract.amountUnit?.id.toString(),
    priceUnitId: contract.contractProduct.priceUnit.id,
    attachments: contract.attachments,
    contractType: contract.contractType,
    automaticSalesApproval: contract.automaticSalesApproval,
    contractManagerId: contract.contractManagerId.toString(),
    weightResponsible: contract.weightResponsible,
    contactEmail: contract.contact.email,
    contactPhone: contract.contact.phone,
    contactCounterpartyEmail: contract.counterpartyContact.email,
    contactCounterpartyPhone: contract.counterpartyContact.phone,
    tradeAtOwnRisk: contract.tradeAtOwnRisk,
    isPaymentReceiverInverted: isPaymentReceiverInverted(
      contract.contractProduct.agreedSellerPricePrUnit[contract.contractProduct.currencyType],
      contract.contractProduct.agreedBuyerPricePrUnit[contract.contractProduct.currencyType]
    ),
    contractDeviations: contract.contractDeviations,
    poNumber: contract.poNumber,
    skipInvoicing: contract.skipInvoicing,
    indexRegulated: contract.indexRegulated,
  };

  return contractInputForm;
}

export function EditContractFormToCreateContract(form: EditContractInput) {
  const createContractObj: CreateContract = {
    name: form.name,
    description: form.description,
    contractBuyers: form.contractBuyers,
    contractSellers: form.contractSellers,
    contractDate: form.contractDate,
    contractStartDate: form.contractStartDate,
    contractEndDate: form.contractType === ContractEndTypeEnum.DATEPERIOD ? form.contractEndDate : null,
    companySpecificProductId: form.companySpecificProductId,
    useTransportServices: form.useTransportServices,
    amount: form.contractType === ContractEndTypeEnum.AMOUNT ? Number(form.amount) : 0,
    agreedBuyerPricePrUnit: form.agreedBuyerPricePrUnit,
    agreedSellerPricePrUnit: form.agreedSellerPricePrUnit,
    selectedServices: form.selectedServices,
    invoiceIntervalId: form.invoiceIntervalId,
    invoiceDueDateId: form.invoiceDueDateId,
    transportResponsible: form.transportResponsible,
    currencyType: form.currencyType,
    comment: form.comment,
    amountUnitId: Number(form.amountUnitId),
    priceUnitId: form.priceUnitId,
    attachments: form.attachments,
    transportFromAddress: form.transportFromAddress,
    transportToAddress: form.transportToAddress,
    automaticSalesApproval: form.automaticSalesApproval,
    tradeAtOwnRisk: form.tradeAtOwnRisk,
    contractManagerId: Number.parseInt(form.contractManagerId),
    weightResponsible: form.weightResponsible,
    contact: {
      email: form.contactEmail,
      phone: form.contactPhone,
    },
    counterpartyContact: {
      email: form.contactCounterpartyEmail,
      phone: form.contactCounterpartyPhone,
    },
    companySpecificServices: form.companySpecificServices.map((service: any) => {
      return {
        companySpecificServiceId: service.companySpecificServiceId,
        price: service.price,
        currencyType: service.currencyType,
      };
    }),
    invoiceInformation: "",
    previousContractId: null,
    contractDeviations: form.contractDeviations,
    template: form.template || false,
    templateName: form.templateName,
    poNumber: form.poNumber ?? null,
    skipInvoicing: form.skipInvoicing,
    indexRegulated: form.indexRegulated,
  };

  if (form.isPaymentReceiverInverted) {
    createContractObj.agreedBuyerPricePrUnit = -Math.abs(form.agreedBuyerPricePrUnit);
    createContractObj.agreedSellerPricePrUnit = -Math.abs(form.agreedSellerPricePrUnit);
  } else {
    createContractObj.agreedBuyerPricePrUnit = Math.abs(form.agreedBuyerPricePrUnit);
    createContractObj.agreedSellerPricePrUnit = Math.abs(form.agreedSellerPricePrUnit);
  }

  return createContractObj;
}
